// TicketQuery.ts
// Defines query parameters for searching/filtering tickets.
// Used for advanced ticket search and filtering.

import {Ticket, TicketType} from "./Ticket";
import {TicketStatus} from "./TicketStatus";
import {User} from "./User";
import {
    isAdmin,
    isAssignedInTicket,
    isAssignedToTicket,
    isInTicket,
    isMine,
    isRoleAssignedToTicket,
    isSupervisor
} from "./UserUtil";
import {Group} from "./Group";
import {getKey} from "./KeyRef";

/** Thrown when a request body cannot be parsed into a TicketQuery; the server answers it with HTTP 400. */
export class InvalidTicketQueryError extends Error {
    readonly status = 400;

    constructor(message: string) {
        super(message);
        this.name = "InvalidTicketQueryError";
        Object.setPrototypeOf(this, InvalidTicketQueryError.prototype);
    }
}

export enum TicketStatusFilter {
    OPENED,
    CLOSED,
    MINE,
    UNASSIGNED,
    JURY, // Filter for tickets where user is a jury member
    PENDING_APPROVAL, // Protected-user gate: reports awaiting staff approval (staff-only)
    REJECTED          // Protected-user gate: reports rejected by staff (staff-only)
}

export function ticketStatusFilterValueOf(value: number): TicketStatusFilter {
    switch (value) {
        case 0:
            return TicketStatusFilter.OPENED;
        case 1:
            return TicketStatusFilter.CLOSED;
        case 2:
            return TicketStatusFilter.MINE;
        case 3:
            return TicketStatusFilter.UNASSIGNED;
        case 4:
            return TicketStatusFilter.JURY;
        case 5:
            return TicketStatusFilter.PENDING_APPROVAL;
        case 6:
            return TicketStatusFilter.REJECTED;
        default:
            throw new InvalidTicketQueryError("Invalid ticket status filter");
    }
}

export function ticketSortValueOf(value: number): TicketSort {
    switch (value) {
        case 0:
            return TicketSort.OLDEST_COMMENT;
        case 1:
            return TicketSort.LATEST_COMMENT;
        case 2:
            return TicketSort.NEWEST;
        case 3:
            return TicketSort.OLDEST;
        case 4:
            return TicketSort.STAFF;
        case 5:
            return TicketSort.STAFF_LATEST;
        default:
            throw new InvalidTicketQueryError("Invalid ticket sort");
    }
}

export interface TicketStatusFilterMetadata {
    /** Localization key for the filter */
    localeKey: string;
    /** A color that represents the filter, named using Bootstrap's color names */
    color: string;
    /** Whether this filter may only be used by admins */
    admin: boolean
}

export const ticketStatusFilterMetadata: Record<TicketStatusFilter, TicketStatusFilterMetadata> = {
    [TicketStatusFilter.OPENED]: { localeKey: "openeds", color: "success", admin: false },
    [TicketStatusFilter.CLOSED]: { localeKey: "closeds", color: "danger", admin: false },
    [TicketStatusFilter.MINE]: { localeKey: "mine", color: "primary", admin: true },
    [TicketStatusFilter.UNASSIGNED]: { localeKey: "unassigned", color: "unassigned", admin: true },
    [TicketStatusFilter.JURY]: { localeKey: "jury_duty", color: "info", admin: false },
    [TicketStatusFilter.PENDING_APPROVAL]: { localeKey: "report.pendingApprovalFilter", color: "warning", admin: true },
    [TicketStatusFilter.REJECTED]: { localeKey: "report.rejectedFilter", color: "danger", admin: true },
}

export const ticketStatusFilterToTicketStatus: Record<TicketStatusFilter, TicketStatus> = {
    [TicketStatusFilter.OPENED]: TicketStatus.OPENED,
    [TicketStatusFilter.CLOSED]: TicketStatus.CLOSED,
    [TicketStatusFilter.MINE]: TicketStatus.OPENED,
    [TicketStatusFilter.UNASSIGNED]: TicketStatus.OPENED,
    [TicketStatusFilter.JURY]: TicketStatus.OPENED,
    [TicketStatusFilter.PENDING_APPROVAL]: TicketStatus.OPENED,
    [TicketStatusFilter.REJECTED]: TicketStatus.CLOSED,
}

export function convertTicketStatusFilterToTicketStatus(filter: TicketStatusFilter, power: number): TicketStatus {
    if (power < 2 && ticketStatusFilterMetadata[filter].admin) {
        return TicketStatus.OPENED;
    }
    return ticketStatusFilterToTicketStatus[filter];
}

export enum TicketSort {
    OLDEST_COMMENT,
    LATEST_COMMENT,
    NEWEST,
    OLDEST,
    STAFF,
    STAFF_LATEST
}

export interface TicketSortMetadata {
    /** Localization key for the sort */
    localeKey: string;
    /** A color that represents the sort, named using Bootstrap's color names */
    color: string;
    /** Whether this sort option is only available to staff users (power >= 2) */
    staffOnly: boolean;
}

export const ticketSortMetadata: Record<TicketSort, TicketSortMetadata> = {
    [TicketSort.OLDEST_COMMENT]: { localeKey: "oldest_comment", color: "primary", staffOnly: false },
    [TicketSort.LATEST_COMMENT]: { localeKey: "latest_comment", color: "primary", staffOnly: false },
    [TicketSort.NEWEST]: { localeKey: "newest", color: "primary", staffOnly: false },
    [TicketSort.OLDEST]: { localeKey: "oldest", color: "primary", staffOnly: false },
    [TicketSort.STAFF]: { localeKey: "staff", color: "warning", staffOnly: true },
    [TicketSort.STAFF_LATEST]: { localeKey: "staff_latest", color: "warning", staffOnly: true }
}

export interface GroupFilter {
    selectAll: boolean
    groups: string[]
}

export function isGroupFilterEqual(filter1: GroupFilter, filter2: GroupFilter): boolean {
    if (filter1.selectAll !== filter2.selectAll) {
        return false;
    }
    if (filter1.selectAll) {
        return true;
    }
    return filter1.groups.length === filter2.groups.length && filter1.groups.every(group => filter2.groups.includes(group));
}

export interface AssignedFilter {
    selectAll: boolean,
    assigned: string[]
}

export function isAssignedFilterEqual(filter1: AssignedFilter, filter2: AssignedFilter): boolean {
    for (const assignee in filter1.assigned) {
        if (filter1.assigned[assignee] !== filter2.assigned[assignee]) {
            return false;
        }
    }
    return true;
}

/**
 * Defines query parameters for searching/filtering tickets.
 * Used for advanced ticket search and filtering.
 */
export interface TicketQuery {
    type: TicketType
    searchFilter: string,
    statusFilter: TicketStatusFilter
    sort: TicketSort
    groupFilter: GroupFilter
    assignedFilter: AssignedFilter,
    isWeb: boolean,
    cursor: number | null
    createdFrom?: number
    createdTo?: number
}

export function parseQueryFromBody(body: unknown): TicketQuery {
    const { type, searchFilter, statusFilter, sort, groupFilter, assignedFilter, isWeb, cursor, createdFrom, createdTo } = body as any;

    const normalizedStatusFilter = ticketStatusFilterValueOf(typeof statusFilter === "string" ? parseInt(statusFilter) : statusFilter);
    const normalizedSort = ticketSortValueOf(typeof sort === "string" ? parseInt(sort) : sort);

    return {
        type,
        searchFilter,
        statusFilter: normalizedStatusFilter,
        sort: normalizedSort,
        groupFilter,
        assignedFilter,
        isWeb,
        cursor,
        ...(createdFrom ? { createdFrom } : {}),
        ...(createdTo ? { createdTo } : {})
    }
}

export function isAssigned(ticket: Ticket, visualId: string) {
    return ticket.assignees.some(assignee => assignee.visualId === visualId)
}

export function checkTicketStatusFilter(user: User, ticket: Ticket, statusFilter: TicketStatusFilter): boolean {
    const statusToMatch = ticketStatusFilterToTicketStatus[statusFilter];
    if (statusToMatch !== ticket.status) {
        return false;
    }

    const statusFilterNum = Number(statusFilter);

    switch (statusFilterNum) {
        case TicketStatusFilter.MINE:
            return isAssignedInTicket(user, ticket);
        case TicketStatusFilter.UNASSIGNED:
            return ticket.assignees.length === 0 && ticket.roleAssignees.length === 0;
        case TicketStatusFilter.JURY:
            // Only show tickets where user is a jury member
            return ticket.juryMembers?.some(m => m._id === user._id) || false;
        case TicketStatusFilter.PENDING_APPROVAL:
            // Protected-user gate: only reports currently held for staff approval
            return ticket.pendingStaffApproval === true;
        case TicketStatusFilter.REJECTED:
            // Protected-user gate: rejected reports kept on record
            return ticket.approvalRejected === true;
        default:
            return true;
    }
}

export function doesTicketMatchQuery(user: User, ticket: Ticket, query: TicketQuery, language: string, groups: Record<string, Group>): boolean {
    return shouldRenderTicket(
        query.type,
        query.statusFilter,
        query.groupFilter,
        query.assignedFilter,
        query.isWeb,
        language,
        user,
        ticket,
        groups
    );
}

function checkGroupFilter(
    lang: string,
    user: User,
    ticket: Ticket,
    groupFilter: GroupFilter,
    groups: Record<string, Group>,
    adminRoles: string[]): boolean {
    if (!isInTicket(user, ticket, groups) && !isAdmin(user, adminRoles)) {
        return false
    }
    if (groupFilter.selectAll) {
        return true
    }
    return groupFilter.groups.some(it => groups[ticket.group].name[lang] == it)
}

function checkAssignedFilter(ticket: Ticket, assignedFilter: AssignedFilter) {
    return assignedFilter.assigned.length == 0 || assignedFilter.assigned.some(visualIdOrRole =>
        isAssignedToTicket(visualIdOrRole, ticket)
        || isRoleAssignedToTicket(visualIdOrRole, ticket
        ))
}

function checkWebFilter(ticket: Ticket, showWeb: boolean, admin: boolean, inTicket: boolean, groups: Record<string, Group>) {
    return ticket.type != TicketType.SUPPORT || (admin ? groups[ticket.group]?.isWeb == showWeb : inTicket)
}

export function shouldRenderTicket(
    type: TicketType,
    statusFilter: TicketStatusFilter,
    groupFilter: GroupFilter,
    assignedFilter: AssignedFilter,
    showWeb: boolean,
    lang: string,
    user: User,
    ticket: Ticket,
    groups: Record<string, Group>): boolean {
    if (ticket.type != type) {
        // Fast fail if the ticket type doesn't match
        return false
    }
    const adminRoles = groups[ticket.group]?.adminRoles?.map(role => getKey(role)) || []
    const inTicket = isInTicket(user, ticket, groups)
    const admin = isAdmin(user, adminRoles) || isSupervisor(user)

    const statusFilterCheck = checkTicketStatusFilter(user, ticket, statusFilter)
    const groupFilterCheck = checkGroupFilter(lang, user, ticket, groupFilter, groups, adminRoles)
    const webFilterCheck = checkWebFilter(ticket, showWeb, admin, inTicket, groups)
    const assignedFilterCheck = checkAssignedFilter(ticket, assignedFilter)

    return statusFilterCheck && groupFilterCheck && webFilterCheck && assignedFilterCheck
}

/**
 * Sort tickets locally based on the current sorting criteria and user information
 * This follows the same logic as the backend sorting
 */
export function sortTickets(
    tickets: Ticket[],
    sort: TicketSort,
    user: User,
    groups: Record<string, Group>
): void {
    const isAssigned = (ticket: Ticket) => ticket.assignees.length > 0 || ticket.roleAssignees.length > 0;
    
    // Calculate priority levels matching backend logic
    const getPriority = (ticket: Ticket): number => {
        const isUnassigned = !isAssigned(ticket);
        
        // Check if unassigned in fraganti ticket
        if (ticket.type === TicketType.REPORT && ticket.inFraganti && isUnassigned) {
            return -3; // Highest priority, unassigned in fraganti tickets
        }
        // Check if directly assigned to user
        if (ticket.assignees.some(assignee => assignee._id === user._id)) {
            return -2; // Second-highest priority
        }
        // Check if assigned to a role the user has
        if (ticket.roleAssignees.some(role => user.adminRoles.includes(role))) {
            return -1; // Third-highest priority
        }
        // Check if unassigned
        if (isUnassigned) {
            return 0; // Mid-priority
        }
        // Assigned to others
        return 1; // Lowest priority
    };
    tickets.sort((a, b) => {
        if (sort === TicketSort.STAFF || sort === TicketSort.STAFF_LATEST) {
            const aPriority = getPriority(a);
            const bPriority = getPriority(b);

            // Sort by priority (lower number = higher priority)
            if (aPriority !== bPriority) {
                return aPriority - bPriority;
            }

            // Only apply inFraganti priority to assigned tickets (unassigned ones are handled by main priority)
            const aInFraganti = a.type === TicketType.REPORT && a.inFraganti && isAssigned(a) ? -1 : 0;
            const bInFraganti = b.type === TicketType.REPORT && b.inFraganti && isAssigned(b) ? -1 : 0;
            if (aInFraganti !== bInFraganti) {
                return aInFraganti - bInFraganti;
            }

            // If same priority and inFraganti status, sort by category weight (higher weight = higher priority)
            const aWeight = groups[a.group]?.categories[a.category]?.weight || 0;
            const bWeight = groups[b.group]?.categories[b.category]?.weight || 0;
            const weightDiff = bWeight - aWeight;
            if (weightDiff !== 0) return weightDiff;
        }
        // For all sorts, use the selected sort field
        switch (sort) {
            case TicketSort.LATEST_COMMENT:
                return b.lastComment - a.lastComment;
            case TicketSort.OLDEST_COMMENT:
                return a.lastComment - b.lastComment;
            case TicketSort.NEWEST:
                return b.timestamp - a.timestamp;
            case TicketSort.OLDEST:
                return a.timestamp - b.timestamp;
            case TicketSort.STAFF:
                // For STAFF, after priority/inFraganti/weight, use oldest comment
                return a.lastComment - b.lastComment;
            case TicketSort.STAFF_LATEST:
                // For STAFF_LATEST, use latest comment
                return b.lastComment - a.lastComment;
            default:
                return 0;
        }
    });
}
